import * as React from 'react';
import { Calendar } from 'lucide-react';
import { Checkbox } from '@/components/forms/Checkbox';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import {
  addOneHour,
  getMinEndTime,
  getMinStartTimeForSessionDate,
} from '@/components/sessions/inperson/session-form-utils';
import type {
  SessionFormData,
  SessionFormFieldChange,
  SessionFormFieldValidation,
} from '@/types/inperson/session.types';

/** Props for {@link TutoringSessionScheduleSection}. */
interface Props {
  form: SessionFormData;
  onFieldChange: SessionFormFieldChange;
  onBlur: (field: string) => void;
  validation: SessionFormFieldValidation;
  disablePastDate?: boolean;
  minDate?: string;
}

/** Date, time, and recurrence for a tutoring session. */
export function TutoringSessionScheduleSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  disablePastDate = true,
  minDate,
}: Readonly<Props>) {
  const lastAutoEndTimeRef = React.useRef<string>('');
  const dateLabel =
    form.recurrenceType === 'several_days_per_week' ? 'Date début récurrence' : 'Date';

  function handleDateChange(value: string): void {
    onFieldChange('sessionDate', value);
  }
  function handleDateBlur(): void {
    onBlur('sessionDate');
  }
  function handleStartTimeChange(value: string): void {
    onFieldChange('startTime', value);
    if (value) {
      const autoEnd = addOneHour(value);
      lastAutoEndTimeRef.current = autoEnd;
      onFieldChange('endTime', autoEnd);
    }
  }
  function handleEndTimeChange(value: string): void {
    onFieldChange('endTime', value);
  }
  function handleHolidaysChange(checked: boolean): void {
    onFieldChange('planDuringHolidays', checked);
  }
  function handleHolidaysBlur(): void {
    onBlur('planDuringHolidays');
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Calendar className="w-4 h-4" />
        Date et horaires
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DatePicker
          label={dateLabel}
          value={form.sessionDate}
          onChange={handleDateChange}
          onBlur={handleDateBlur}
          state={validation.getFieldState('sessionDate')}
          error={validation.getFieldError('sessionDate')}
          required
          inputSize="sm"
          disablePast={minDate ? false : disablePastDate}
          minDate={minDate}
        />
        <TimePicker
          label="Heure de début"
          value={form.startTime}
          onChange={handleStartTimeChange}
          error={validation.getFieldError('startTime')}
          required
          placeholder="hh:mm"
          minTime={getMinStartTimeForSessionDate(form.sessionDate)}
          step={1}
          showQuickSelect
          inputSize="sm"
        />
        <TimePicker
          label="Heure de fin"
          value={form.endTime}
          onChange={handleEndTimeChange}
          error={validation.getFieldError('endTime')}
          required
          placeholder="hh:mm"
          minTime={getMinEndTime(form.startTime)}
          step={1}
          showQuickSelect
          inputSize="sm"
        />
      </div>
      <div className="mt-4 [&_.form-label]:text-sm [&_.form-helper-text]:text-xs">
        <Checkbox
          label="Autoriser la planification pendant les vacances et jours fériés"
          checked={form.planDuringHolidays}
          onCheckedChange={handleHolidaysChange}
          onBlur={handleHolidaysBlur}
          helperText="Si décoché, les dates couvertes par le calendrier vacances / jours fériés de l'entreprise seront refusées."
          size="sm"
        />
      </div>
    </div>
  );
}
